import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AlertBox from '../../Components/AlertBox';

const ShiftManagement = () => {
  const [activeTab, setActiveTab] = useState('shiftSetup');
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAlert, setShowAlert] = useState(null);
  const [loading, setLoading] = useState({
    departments: true,
    employees: false,
    shifts: false
  });
  const [error, setError] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(null);

  // Utility function to convert 24-hour time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';

    let cleanTime = time24.toString().trim();
    let isPM = false;
    let hasAMPM = false;

    if (/\bPM\b/i.test(cleanTime)) {
      isPM = true;
      hasAMPM = true;
      cleanTime = cleanTime.replace(/\s*PM\s*/gi, '');
    } else if (/\bAM\b/i.test(cleanTime)) {
      hasAMPM = true;
      cleanTime = cleanTime.replace(/\s*AM\s*/gi, '');
    }

    try {
      const [hours, minutes] = cleanTime.split(':');
      let hour = parseInt(hours, 10);
      const minute = minutes || '00';

      if (isNaN(hour)) return time24;

      if (hasAMPM) {
        if (hour === 0) hour = 12;
        return `${hour}:${minute} ${isPM ? 'PM' : 'AM'}`;
      }

      if (hour === 0) {
        return `12:${minute} AM`;
      } else if (hour < 12) {
        return `${hour}:${minute} AM`;
      } else if (hour === 12) {
        return `12:${minute} PM`;
      } else {
        return `${hour - 12}:${minute} PM`;
      }
    } catch (error) {
      console.error('Error converting time:', error);
      return time24;
    }
  };

  // State for shift setup
  const [shiftSetup, setShiftSetup] = useState({
    name: '',
    startTime: '',
    endTime: ''
  });

  const [shiftAssignment, setShiftAssignment] = useState({
    company: localStorage.getItem('selectedCompanyName') || 'My Company',
    companyId: localStorage.getItem('selectedCompanyId') || '',
    department: '',
    employee: '',
    shift: '',
    date: ''
  });

  const [editingShift, setEditingShift] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [shifts, setShifts] = useState([]);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      const companyId = localStorage.getItem('selectedCompanyId');
      const token = localStorage.getItem('authToken');

      if (!companyId || !token) {
        setError('No company selected or user not authenticated');
        setLoading(prev => ({ ...prev, departments: false }));
        return;
      }

      try {
        const response = await axios.get(
          `https://greenyellow-nightingale-241896.hostingersite.com/api/departments/company/${companyId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDepartments(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching departments:', err);
        setError('Failed to fetch departments');
        setDepartments([]);
      } finally {
        setLoading(prev => ({ ...prev, departments: false }));
      }
    };

    fetchDepartments();
  }, [shiftAssignment.companyId]);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem('authToken');
      const companyId = localStorage.getItem('selectedCompanyId');

      if (!token) {
        setError('No access token found. Please log in.');
        setLoading(prev => ({ ...prev, employees: false }));
        return;
      }

      if (!companyId) {
        setError('No company selected. Please select a company first.');
        setLoading(prev => ({ ...prev, employees: false }));
        return;
      }

      try {
        setLoading(prev => ({ ...prev, employees: true }));
        const response = await axios.get(
          `https://greenyellow-nightingale-241896.hostingersite.com/api/employees/company/${companyId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setEmployees(response.data);
      } catch (err) {
        console.error('Error fetching employees:', err.response?.data || err.message);
        setError(`Failed to fetch employees: ${err.response?.data?.message || err.message || 'Unable to connect to server'}`);
        setEmployees([]);
      } finally {
        setLoading(prev => ({ ...prev, employees: false }));
      }
    };

    fetchEmployees();
  }, []);

  // Fetch shifts
  const fetchShifts = useCallback(async () => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      setError('No access token found. Please log in.');
      setLoading(prev => ({ ...prev, shifts: false }));
      return;
    }
    try {
      setLoading(prev => ({ ...prev, shifts: true }));
      const response = await axios.get(
        'https://greenyellow-nightingale-241896.hostingersite.com/api/shifts/',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShifts(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setError('Failed to fetch shifts');
      setShifts([]);
    } finally {
      setLoading(prev => ({ ...prev, shifts: false }));
    }
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  // Handle create shift confirmation
  const handleCreateShiftClick = (e) => {
    e.preventDefault();

    if (!shiftSetup.name || !shiftSetup.startTime || !shiftSetup.endTime) {
      setError('Please fill in all shift details');
      return;
    }

    setConfirmAction({
      type: 'create',
      message: `Are you sure you want to create the shift "${shiftSetup.name}"?`,
      data: { ...shiftSetup }
    });
  };

  // Handle edit shift confirmation
  const handleEditShiftClick = (e) => {
    e.preventDefault();

    if (!shiftSetup.name || !shiftSetup.startTime || !shiftSetup.endTime) {
      setError('Please fill in all shift details');
      return;
    }

    setConfirmAction({
      type: 'edit',
      message: `Are you sure you want to update the shift "${shiftSetup.name}"?`,
      data: { ...shiftSetup },
      shiftId: editingShift._id
    });
  };

  // Handle delete shift confirmation
  const deleteShift = (shiftId, shiftName) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the shift "${shiftName}"?`,
      shiftId: shiftId
    });
  };

  // Execute the confirmed action
  const handleConfirmAction = async () => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      setError('No access token found. Please log in.');
      setConfirmAction(null);
      return;
    }

    try {
      setLoading(prev => ({ ...prev, shifts: true }));

      switch (confirmAction.type) {
        case 'create':
          await axios.post(
            'https://greenyellow-nightingale-241896.hostingersite.com/api/shifts/',
            confirmAction.data,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setShowSuccessAlert(`Shift "${confirmAction.data.name}" created successfully!`);
          break;

        case 'edit':
          await axios.put(
            `https://greenyellow-nightingale-241896.hostingersite.com/api/shifts/${confirmAction.shiftId}`,
            confirmAction.data,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setShowSuccessAlert(`Shift "${confirmAction.data.name}" updated successfully!`);
          setEditingShift(null);
          break;

        case 'delete':
          await axios.delete(
            `https://greenyellow-nightingale-241896.hostingersite.com/api/shifts/${confirmAction.shiftId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setShowSuccessAlert('Shift deleted successfully!');
          break;

        default:
          break;
      }

      // Refresh shifts list
      await fetchShifts();

      // Reset form if it was a create or edit operation
      if (confirmAction.type === 'create' || confirmAction.type === 'edit') {
        setShiftSetup({ name: '', startTime: '', endTime: '' });
      }

    } catch (err) {
      console.error('Error performing shift operation:', err);
      setError(`Failed to ${confirmAction.type} shift: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, shifts: false }));
      setConfirmAction(null);
    }
  };

  // Close confirmation dialog
  const handleCloseAlert = () => {
    setConfirmAction(null);
  };

  // Close success alert
  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(null);
  };

  // Cancel edit
  const cancelEdit = () => {
    setShiftSetup({ name: '', startTime: '', endTime: '' });
    setEditingShift(null);
  };

  // Edit shift function
  const editShift = (shift) => {
    setShiftSetup({
      name: shift.name,
      startTime: shift.startTime,
      endTime: shift.endTime
    });
    setEditingShift(shift);
    setActiveTab('shiftSetup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-2 md:p-4">
      <div className="max-w-full mx-auto">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="flex flex-wrap">
            <button
              className={`px-6 py-4 font-medium flex items-center ${activeTab === 'shiftSetup' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              onClick={() => setActiveTab('shiftSetup')}
            >
              Shift Setup
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                {shifts.length}
              </span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Shift Setup Tab */}
          {activeTab === 'shiftSetup' && (
            <div>
              <div className="flex items-center mb-6">
                <div className="p-2 rounded-md bg-blue-100 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800">Shift Setup</h2>
              </div>

              <form onSubmit={editingShift ? handleEditShiftClick : handleCreateShiftClick} className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-gray-700 mb-2">Shift Name</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={shiftSetup.name}
                      onChange={(e) => setShiftSetup({ ...shiftSetup, name: e.target.value })}
                      placeholder="e.g., Morning Shift"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 mb-2">Start Time</label>
                      <input
                        type="time"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={shiftSetup.startTime}
                        onChange={(e) => setShiftSetup({ ...shiftSetup, startTime: e.target.value })}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 mb-2">End Time</label>
                      <input
                        type="time"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={shiftSetup.endTime}
                        onChange={(e) => setShiftSetup({ ...shiftSetup, endTime: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200 flex items-center"
                    disabled={loading.shifts}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={editingShift ? "M5 13l4 4L19 7" : "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"} />
                    </svg>
                    {editingShift ? 'Update Shift' : 'Create Shift'}
                  </button>

                  {editingShift && (
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-200"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>

              <div>
                <h3 className="text-lg font-medium text-gray-800 mb-4">Existing Shifts</h3>
                {loading.shifts ? (
                  <p className="text-gray-500 text-center py-4">Loading shifts...</p>
                ) : shifts.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No shifts created yet.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Shift Name</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Start Time</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">End Time</th>
                          <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {shifts.map((shift) => (
                          <tr key={shift._id} className="hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">{shift.name}</td>
                            <td className="py-3 px-4 font-medium text-blue-600">{formatTimeTo12Hour(shift.startTime)}</td>
                            <td className="py-3 px-4 font-medium text-green-600">{formatTimeTo12Hour(shift.endTime)}</td>
                            <td className="py-3 px-4">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => editShift(shift)}
                                  className="text-blue-600 hover:text-blue-800"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteShift(shift._id, shift.name)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Confirmation Alert Dialog */}
          {confirmAction && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Confirmation</h2>
                <p className="text-gray-700 mb-6">{confirmAction.message}</p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={handleConfirmAction}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
                    disabled={loading.shifts}
                  >
                    {loading.shifts ? 'Processing...' : 'Confirm'}
                  </button>
                  <button
                    onClick={handleCloseAlert}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition"
                    disabled={loading.shifts}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Success Alert Dialog */}
          {showSuccessAlert && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white rounded-2xl shadow-lg p-6 w-[90%] max-w-sm text-center">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Success</h2>
                <p className="text-gray-700 mb-6">{showSuccessAlert}</p>
                <div className="flex justify-center">
                  <button
                    onClick={handleCloseSuccessAlert}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftManagement;

